import type {
  CampaignDraft,
  BriefJob,
  FunnelStageStrategy,
  NarrativeAngle,
} from "@/lib/campaign-draft";
import { displayUrl, normalizeExampleImage } from "@shared/adImage";

function firstExampleImageUrl(raw: unknown): string | null {
  if (!Array.isArray(raw)) return null;
  for (const entry of raw) {
    const url = displayUrl(normalizeExampleImage(entry));
    if (url) return url;
  }
  return null;
}

export const FUNNEL_STAGE_ORDER = [
  "Awareness",
  "Knowledge",
  "Liking",
  "Preference",
  "Conviction",
  "Purchase",
];

const STAGE_GROUP_TO_NAME: Record<string, string> = {
  tof: "Awareness",
  mof: "Preference",
  bof: "Purchase",
  awareness: "Awareness",
  knowledge: "Knowledge",
  liking: "Liking",
  preference: "Preference",
  conviction: "Conviction",
  purchase: "Purchase",
};

function resolveStage(
  funnelStageId: string,
  funnelStages: FunnelStageStrategy[]
): { stageName: string; stageOrder: number; stageStrategy: FunnelStageStrategy | null } {
  const lower = funnelStageId.toLowerCase();

  const matched = funnelStages.find(
    (s) =>
      s.stage.toLowerCase() === lower ||
      (s.stage_group && s.stage_group.toLowerCase() === lower)
  );

  if (matched) {
    const order = FUNNEL_STAGE_ORDER.indexOf(matched.stage);
    return {
      stageName: matched.stage,
      stageOrder: order >= 0 ? order : 99,
      stageStrategy: matched,
    };
  }

  const mapped = STAGE_GROUP_TO_NAME[lower] ?? funnelStageId;
  const order = FUNNEL_STAGE_ORDER.indexOf(mapped);
  return { stageName: mapped, stageOrder: order >= 0 ? order : 99, stageStrategy: null };
}

function pickAngles(
  stageStrategy: FunnelStageStrategy | null,
  allAngles: NarrativeAngle[]
): NarrativeAngle[] {
  if (stageStrategy && stageStrategy.recommended_angle_ids?.length > 0) {
    const recommended = stageStrategy.recommended_angle_ids
      .map((id) => allAngles.find((a) => a.id === id))
      .filter(Boolean) as NarrativeAngle[];
    if (recommended.length > 0) return recommended;
  }
  return allAngles;
}

export function buildCreativeBriefJobs(draft: CampaignDraft): BriefJob[] {
  const { adUnitSelections, funnelStrategy, narrativeAngles } = draft;
  const funnelStages = funnelStrategy?.stages ?? [];
  const jobs: BriefJob[] = [];

  const now = Date.now();
  let globalIndex = 0;

  for (const sel of adUnitSelections) {
    const { stageName, stageOrder, stageStrategy } = resolveStage(
      sel.funnelStageId,
      funnelStages
    );
    const availableAngles = pickAngles(stageStrategy, narrativeAngles);

    for (let i = 0; i < sel.quantity; i++) {
      const angle = availableAngles.length > 0
        ? availableAngles[(globalIndex + i) % availableAngles.length]
        : null;

      jobs.push({
        jobId: `job-${sel.adUnitId}-${i}-${now}-${globalIndex + i}`,
        stage: stageName,
        stageOrder,
        adUnitId: sel.adUnitId,
        adUnitName: sel.adUnit.name,
        adUnit: sel.adUnit,
        funnelStageId: sel.funnelStageId,
        format: sel.adUnit.format,
        instanceIndex: i,
        angleId: angle?.id ?? null,
        angleName: angle?.name ?? null,
        referenceImageUrl:
          sel.selectedImageUrl ?? firstExampleImageUrl(sel.adUnit.exampleImages),
        status: "pending",
        brief: null,
        error: null,
        generatedImageUrl: null,
        imageStatus: "idle",
        imageError: null,
      });
    }

    globalIndex += sel.quantity;
  }

  jobs.sort((a, b) => {
    if (a.stageOrder !== b.stageOrder) return a.stageOrder - b.stageOrder;
    return a.adUnitName.localeCompare(b.adUnitName);
  });

  return jobs;
}
